import { useState } from "react";
import { MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { WhatsAppIcon, buildWhatsAppLink } from "./Header";

export default function ContactSection({ whatsappNumber }) {
  const [form, setForm] = useState({ name: "", phone: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", form);
      toast.success("Mensagem enviada! A loja retorna em breve.");
      setSent(true);
      setForm({ name: "", phone: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" className="py-24 px-6 max-w-6xl mx-auto border-t border-[#E8E2D9]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--brand)] block mb-2">Atendimento</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight mb-4">Fale com a Dra.Cell</h2>
          <p className="text-[#5A534C] leading-relaxed mb-6">
            Orçamento de reparo, dúvida sobre produto ou acompanhamento de pedido — mande sua mensagem aqui que ela chega
            direto no painel da loja, ou chame no WhatsApp para resposta na hora.
          </p>
          <a
            href={buildWhatsAppLink(whatsappNumber, "Olá! Vim pelo site e quero atendimento.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-full font-display text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            <WhatsAppIcon /> Chamar no WhatsApp
          </a>
        </div>

        <div className="bg-[#F2EDE4] rounded-3xl border border-[#E8E2D9] p-8" data-testid="contact-form-card">
          {sent ? (
            <div className="text-center py-8" data-testid="contact-sent-message">
              <MessageCircle className="w-10 h-10 text-[var(--brand)] mx-auto mb-3" />
              <p className="font-display font-semibold">Mensagem recebida!</p>
              <p className="text-sm text-[#6B645C] mt-1">A Dra.Cell vai te responder em breve.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <input type="text" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required minLength={2} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Seu nome" data-testid="contact-name-input"
                  className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
                <input required minLength={8} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/[^\d()\s-]/g, "") }))}
                  placeholder="Seu WhatsApp (com DDD)" data-testid="contact-phone-input"
                  className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
              </div>
              <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="Assunto ou modelo do aparelho (ex: iPhone 11 — troca de tela)" data-testid="contact-subject-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
              <textarea required minLength={3} rows={3} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="Escreva sua mensagem..." data-testid="contact-message-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors resize-none" />
              <button type="submit" disabled={sending} data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1816] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Enviando..." : "Enviar Mensagem"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
